import React from 'react';
import { ContentProps } from '../../models/TopLevelTemplateComponentProps';
import { newContentId } from '../../services/TemplateUtils';
import LinkifiedHtml from '../LinkifiedHtml';
import { StyledTypography } from '../styles/styles';
import { tableCellRenderProps } from './tableCellClassName';
import type { TableCell } from './tableTypes';

/** Coerce string / mixed cell content into ContentProps (or ContentProps[]). */
export function normalizeCellContent(cell: TableCell): TableCell {
  if (
    typeof cell.content === 'object' &&
    cell.content !== null &&
    !Array.isArray(cell.content) &&
    'id' in cell.content &&
    'content' in cell.content
  ) {
    return cell;
  }

  if (Array.isArray(cell.content)) {
    return {
      ...cell,
      content: cell.content.map((item) => {
        if (typeof item === 'string') {
          return { id: newContentId(), content: item };
        }
        return item;
      }),
    };
  }

  if (typeof (cell.content as unknown) === 'string') {
    return {
      ...cell,
      content: { id: newContentId(), content: cell.content as unknown as string },
    };
  }

  return cell;
}

function CellReadView({ cell }: { cell: TableCell }) {
  if (Array.isArray(cell.content)) {
    return (
      <>
        {cell.content.map((contentItem) => (
          <LinkifiedHtml key={contentItem.id} as={StyledTypography} html={contentItem.content} />
        ))}
      </>
    );
  }
  return <LinkifiedHtml as={StyledTypography} html={(cell.content as ContentProps).content} />;
}

export default function TableCellView({
  cell,
  isHeaderRow = false,
  isFirstColumn = false,
}: {
  cell: TableCell;
  isHeaderRow?: boolean;
  isFirstColumn?: boolean;
}) {
  const normalizedCell = normalizeCellContent(cell);
  const cellProps = tableCellRenderProps(cell, isHeaderRow, isFirstColumn);
  const Tag = isHeaderRow ? 'th' : 'td';

  return (
    <Tag colSpan={normalizedCell.colSpan} rowSpan={normalizedCell.rowSpan} {...cellProps}>
      <CellReadView cell={normalizedCell} />
    </Tag>
  );
}
